import {
  utcMondayMs,
  convertToEndOfWeekUTC,
  convertToStartOfMonthUTC,
  formatDate,
  DateFormatType,
} from "~/utils/datetime"
import { getStoredFiscalConfig } from "~/stores/fiscal-config-store"
import { subYears } from "date-fns"

/**
 * NRF-style retail fiscal calendar (4-5-4 / 4-4-5 / 5-4-4), anchored so fiscal year N starts on
 * the Monday of the week containing Jan 1 of year N. Weeks are Monday-start to match the app's
 * weekly data.
 *
 * Each fiscal period is a whole number of weeks (4 or 5) in a fixed per-quarter pattern, so the
 * same week-of-year index always maps to the same period every year — giving exact year-over-year
 * comparability regardless of how ISO weeks drift against the calendar. Fiscal years with 53 weeks
 * put the extra week in period 11.
 */

export const MS_PER_DAY = 86_400_000
const MS_PER_WEEK = 7 * MS_PER_DAY

/**
 * Supported fiscal-calendar patterns. A company's `fiscalType` (nullable) selects one;
 * `null` means no fiscal calendar — fall back to ISO week numbering. 4-5-4 is the default
 * (chosen by AP). The three differ only in how the 13 weeks of each quarter split into months.
 */
export const FISCAL_TYPES = ["4-5-4", "4-4-5", "5-4-4"] as const
export type FiscalType = (typeof FISCAL_TYPES)[number]

/** Weeks per fiscal month within a 13-week quarter, keyed by pattern. */
const QUARTER_WEEK_PATTERN = {
  "4-5-4": [4, 5, 4],
  "4-4-5": [4, 4, 5],
  "5-4-4": [5, 4, 4],
} as const satisfies Record<FiscalType, readonly [number, number, number]>

/** Weeks in each of the 12 fiscal months for a standard 52-week year of the given pattern. */
export function getFiscalMonthWeeks(fiscalType: FiscalType): number[] {
  const quarter = QUARTER_WEEK_PATTERN[fiscalType]
  return Array.from({ length: 4 }, () => quarter).flat()
}

/** Weeks per fiscal period (0-11), 4-5-4 per quarter. Sums to 52. Equals `getFiscalMonthWeeks("4-5-4")`. */
export const RETAIL_PERIOD_WEEKS = [4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5, 4] as const

export type FiscalDescriptor = {
  fiscalYear: number
  fiscalPeriod: number
  weekIndex: number
}

/**
 * The period functions below take an optional `FiscalConfig`. With a config, the fiscal year is
 * anchored on the Monday of the week containing the company's `fiscalDate` and the 12 periods follow
 * its `fiscalType` (4-5-4 / 4-4-5 / 5-4-4). When the argument is omitted, they read the signed-in
 * company's config from the fiscal-config store (hydrated client-side only, so on the server and in
 * tests omission means "no config"). With no config — omitted and store empty, or an explicit
 * `null` — they fall back to the legacy retail calendar: anchored on the Monday of the week
 * containing Jan 1, with the 4-5-4 pattern. Pass an explicit `null` to force calendar behavior
 * regardless of the company's fiscal setting.
 */

/** Cumulative week index at the start of each fiscal period for a per-period week-count array. */
function periodStartWeeks(periodWeeks: readonly number[]): number[] {
  return periodWeeks.reduce<number[]>((acc, _w, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1]! + periodWeeks[i - 1]!)
    return acc
  }, [])
}

/** Per-fiscal-period week counts for a config's pattern, or the default 4-5-4 when no config. */
function periodWeeksFor(config?: FiscalConfig | null): readonly number[] {
  return config ? getFiscalMonthWeeks(config.fiscalType) : RETAIL_PERIOD_WEEKS
}

/** Monday the fiscal year starts on: the week containing the config anchor (month/day), or Jan 1. */
export function fiscalYearStartMonday(
  fiscalYear: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): Date {
  const month = config ? config.anchor.getUTCMonth() : 0
  const day = config ? config.anchor.getUTCDate() : 1
  return new Date(utcMondayMs(new Date(Date.UTC(fiscalYear, month, day))))
}

/** True when the fiscal year spans 53 weeks (next year's anchor is 371 days out, not 364). */
export function is53WeekYear(
  fiscalYear: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): boolean {
  const span =
    fiscalYearStartMonday(fiscalYear + 1, config).getTime() -
    fiscalYearStartMonday(fiscalYear, config).getTime()
  return Math.round(span / MS_PER_WEEK) === 53
}

function periodFromWeekIndex(weekIndex: number, config?: FiscalConfig | null): number {
  const starts = periodStartWeeks(periodWeeksFor(config))
  for (let p = starts.length - 1; p >= 0; p--) {
    if (weekIndex >= starts[p]!) return p
  }
  return 0
}

/** Map a date to its fiscal year, period (0-11), and week index within the fiscal year. */
export function dateToFiscal(
  date: Date,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): FiscalDescriptor {
  const mondayMs = utcMondayMs(date)
  let fiscalYear = date.getUTCFullYear()
  if (mondayMs < fiscalYearStartMonday(fiscalYear, config).getTime()) {
    fiscalYear -= 1
  } else if (mondayMs >= fiscalYearStartMonday(fiscalYear + 1, config).getTime()) {
    fiscalYear += 1
  }
  const weekIndex = Math.round(
    (mondayMs - fiscalYearStartMonday(fiscalYear, config).getTime()) / MS_PER_WEEK,
  )
  return { fiscalYear, fiscalPeriod: periodFromWeekIndex(weekIndex, config), weekIndex }
}

/** Monday dates of every week in a fiscal period (4 or 5; +1 for the last period in a 53-week year). */
export function fiscalPeriodWeekMondays(
  fiscalYear: number,
  period: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): Date[] {
  const weeks = periodWeeksFor(config)
  const starts = periodStartWeeks(weeks)
  const startMs =
    fiscalYearStartMonday(fiscalYear, config).getTime() + starts[period]! * MS_PER_WEEK
  const count =
    weeks[period]! + (period === weeks.length - 1 && is53WeekYear(fiscalYear, config) ? 1 : 0)
  return Array.from({ length: count }, (_, i) => new Date(startMs + i * MS_PER_WEEK))
}

/** First Monday → last Sunday of a fiscal period. */
export function fiscalPeriodRange(
  fiscalYear: number,
  period: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): { start: Date; end: Date } {
  const mondays = fiscalPeriodWeekMondays(fiscalYear, period, config)
  const start = mondays[0]!
  const end = convertToEndOfWeekUTC(mondays[mondays.length - 1]!)
  return { start, end }
}

/**
 * Widen a date range outward to whole fiscal periods: start moves back to its period's first
 * Monday, end forward to its period's last Sunday. Fiscal periods are whole Mon–Sun weeks, so a
 * snapped range contains only complete periods — weekly data fetched for it aggregates into exact
 * period totals with no partial buckets at the edges. Identity when no fiscal config applies.
 */
export function snapRangeToFiscalPeriods(
  start: Date,
  end: Date,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): { start: Date; end: Date } {
  if (!config) return { start, end }
  const first = dateToFiscal(start, config)
  const last = dateToFiscal(end, config)
  return {
    start: fiscalPeriodRange(first.fiscalYear, first.fiscalPeriod, config).start,
    end: fiscalPeriodRange(last.fiscalYear, last.fiscalPeriod, config).end,
  }
}

/**
 * The 1st of the calendar month a fiscal period represents — its chart x-axis position. Period `p`
 * maps to calendar month `(namingMonth + p)`, rolling into the next year past December. The naming
 * month is the month of the anchor's seventh day, i.e. the anchor week's Sunday for a Monday anchor
 * (NRF convention: a year anchored on 27 Apr names its first period May); with no config it is
 * January, so the legacy calendar maps period `p` to month `p` of `fiscalYear`. The period's real
 * first-Monday (`fiscalPeriodRange().start`) can fall in the previous calendar month, so it must
 * not be used to position monthly points.
 */
export function fiscalPeriodMonthStart(
  fiscalYear: number,
  period: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): Date {
  if (!config) return new Date(Date.UTC(fiscalYear, period, 1))
  const { month, yearOffset } = namingMonth(config)
  return new Date(Date.UTC(fiscalYear + yearOffset, month + period, 1))
}

const NAMING_REFERENCE_YEAR = 2001

/**
 * Month the fiscal year is named by, and 1 when it falls in the calendar year after the anchor's
 * (a late-December anchor such as 29 Dec names FY`N` by January of `N + 1`). Uses only the
 * anchor's month and day, so the year it is stored in never shifts the result.
 */
function namingMonth(config: FiscalConfig): { month: number; yearOffset: number } {
  const naming = new Date(
    Date.UTC(NAMING_REFERENCE_YEAR, config.anchor.getUTCMonth(), config.anchor.getUTCDate() + 6),
  )
  return {
    month: naming.getUTCMonth(),
    yearOffset: naming.getUTCFullYear() - NAMING_REFERENCE_YEAR,
  }
}

/** Calendar year FY`fiscalYear` is labelled by in the UI: the anchor year, or the next one for a late-December anchor. */
export function fiscalYearLabel(
  fiscalYear: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return config ? fiscalYear + namingMonth(config).yearOffset : fiscalYear
}

/** `FY2026`-style name for FY`fiscalYear`, by its `fiscalYearLabel`. */
export function formatFiscalYear(
  fiscalYear: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): string {
  return `FY${fiscalYearLabel(fiscalYear, config)}`
}

/** Inverse of `fiscalYearLabel`: the fiscal year a year picker's `labelYear` stands for. */
export function fiscalYearFromLabel(
  labelYear: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return config ? labelYear - namingMonth(config).yearOffset : labelYear
}

/** Calendar year of `period`'s month minus `fiscalYear`: 0, or 1 once the period's month is in the next calendar year. */
export function calendarYearOffset(
  fiscalYear: number,
  period: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return fiscalPeriodMonthStart(fiscalYear, period, config).getUTCFullYear() - fiscalYear
}

/**
 * Inverse of `fiscalPeriodMonthStart`: the first-Monday → last-Sunday range of the fiscal period a
 * monthly bucket keyed on `monthStart` (1st of a calendar month) actually sums.
 */
export function fiscalPeriodRangeForMonth(
  monthStart: Date,
  config: FiscalConfig,
): { start: Date; end: Date } {
  const year = monthStart.getUTCFullYear()
  const key = Date.UTC(year, monthStart.getUTCMonth(), 1)
  for (const fiscalYear of [year - 1, year, year + 1]) {
    for (let period = 0; period < 12; period++) {
      if (fiscalPeriodMonthStart(fiscalYear, period, config).getTime() === key) {
        return fiscalPeriodRange(fiscalYear, period, config)
      }
    }
  }
  throw new Error(`No fiscal period maps to ${monthStart.toISOString()}`)
}

export type WeekPeriod = {
  /** 1st of the calendar month the period represents — the chart x position for the whole period. */
  monthStart: number
  /** Week starts in the period, ascending. */
  weeks: number[]
}

const fiscalMonthStartMs = (ms: number, config: FiscalConfig | null) => {
  const { fiscalYear, fiscalPeriod } = dateToFiscal(new Date(ms), config)
  return fiscalPeriodMonthStart(fiscalYear, fiscalPeriod, config).getTime()
}

/**
 * Groups week starts into months of whole weeks, for every company — the Demand 2.0 forecast is
 * weekly, so a monthly edit would otherwise have to write fractional weeks. With no fiscal calendar
 * that means `dateToFiscal`'s default 4-5-4, deliberately unlike `convertWeeklyToFullMonthly`, which
 * day-distributes into calendar months.
 */
export function groupWeeksIntoPeriods(
  weekStarts: number[],
  config: FiscalConfig | null = getStoredFiscalConfig(),
): WeekPeriod[] {
  const byMonth = new Map<number, number[]>()
  for (const ms of [...weekStarts].sort((a, b) => a - b)) {
    const monthStart = fiscalMonthStartMs(ms, config)
    const weeks = byMonth.get(monthStart)
    if (weeks) weeks.push(ms)
    else byMonth.set(monthStart, [ms])
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => a - b)
    .map(([monthStart, weeks]) => ({ monthStart, weeks }))
}

/**
 * Sums a weekly `[weekStartMs, value]` series into the months a monthly table shows: fiscal periods
 * with a fiscal calendar, calendar months without — the two cases `resolveFiscalMonthlyWindow` serves
 * the sales endpoint. Not `groupWeeksIntoPeriods`, whose whole weeks would put a chart on different
 * months than the table beside it. A month is `null` only when EVERY week in it is null, so a fully
 * uncovered month stays a gap rather than a fake 0.
 */
export function sumWeeklyIntoPeriods(
  entries: [number, number | null][],
  config: FiscalConfig | null = getStoredFiscalConfig(),
): [number, number | null][] {
  const byMonth = new Map<number, number | null>()
  for (const [ms, value] of entries) {
    const monthMs = config
      ? fiscalMonthStartMs(ms, config)
      : convertToStartOfMonthUTC(new Date(ms)).getTime()
    if (value == null) {
      if (!byMonth.has(monthMs)) byMonth.set(monthMs, null)
    } else {
      byMonth.set(monthMs, (byMonth.get(monthMs) ?? 0) + value)
    }
  }
  return [...byMonth.entries()].sort(([a], [b]) => a - b)
}

/**
 * Last fiscal year's counterpart of a horizon week, by position in the fiscal year — a fixed 52-week
 * shift lands a week out either side of a 53-week year. Always resolve year-over-year this way round:
 * mapping last year's weeks forward instead collides after a 53-week year and drops one.
 */
export function lastYearWeekMs(weekStartMs: number, config: FiscalConfig | null): number {
  if (!config) return weekStartMs - 52 * MS_PER_WEEK
  const { fiscalYear, weekIndex } = dateToFiscal(new Date(weekStartMs), config)
  return fiscalYearStartMonday(fiscalYear - 1, config).getTime() + weekIndex * MS_PER_WEEK
}

// ---------------------------------------------------------------------------
// Per-company fiscal week numbering (REKD-646)
//
// Label-only: stored data stays keyed by its real Monday date; we only re-number
// the *displayed* week. A company's `fiscalDate` anchors week 1 (e.g. 28 Apr) and
// week numbers count whole Monday-weeks from there. With no fiscal config, callers
// fall back to ISO week numbers. NOTE: week boundaries remain Monday–Sunday — this
// is correct only when the customer's fiscal weeks are Monday-start (see REKD-646).
// ---------------------------------------------------------------------------

/** Resolved fiscal calendar for a company. `anchor` is the start date of week 1 (any year). */
export type FiscalConfig = {
  fiscalType: FiscalType
  anchor: Date
}

/** A date mapped to a company's fiscal calendar. `week` is 1-indexed; `fiscalYear` is the calendar year the fiscal year starts in. */
export type FiscalWeek = {
  fiscalYear: number
  week: number
}

type CompanyFiscalFields = {
  fiscalType: string | null
  fiscalDate: Date | string | null
}

/**
 * Build a `FiscalConfig` from a company row, or `null` when the fiscal calendar is off.
 * Both `fiscalType` and `fiscalDate` must be set and valid. Safe to call server- or client-side.
 *
 * `fiscalDate` is a bare `date` (OID 1082) and no parser is registered for it, so pg builds the Date
 * at LOCAL midnight — east of UTC `getUTCDate()` then reads the day before, and since the anchor is
 * Monday-based `utcMondayMs` snaps a whole week early. Take the calendar day off such a Date by its
 * local fields and re-express it at UTC midnight; an ISO string is already UTC midnight.
 */
export function getFiscalConfig(
  company: CompanyFiscalFields | null | undefined,
): FiscalConfig | null {
  if (!company?.fiscalType || !company.fiscalDate) return null
  if (!(FISCAL_TYPES as readonly string[]).includes(company.fiscalType)) return null
  const raw = company.fiscalDate
  const anchor =
    raw instanceof Date
      ? new Date(Date.UTC(raw.getFullYear(), raw.getMonth(), raw.getDate()))
      : new Date(raw)
  if (Number.isNaN(anchor.getTime())) return null
  return { fiscalType: company.fiscalType as FiscalType, anchor }
}

/** UTC-Monday (ms) of the week containing the anchor's month/day in the given calendar year. */
function fiscalYearStartMondayMs(anchor: Date, calendarYear: number): number {
  return utcMondayMs(new Date(Date.UTC(calendarYear, anchor.getUTCMonth(), anchor.getUTCDate())))
}

/** The Monday fiscal week 1 of `fiscalYear` starts on, for a company's fiscal calendar. */
export function fiscalYearStartDate(fiscalYear: number, config: FiscalConfig): Date {
  return new Date(fiscalYearStartMondayMs(config.anchor, fiscalYear))
}

/**
 * The range FY`year` actually covers for this company.
 *
 * A tenant on a retail calendar (4-5-4 and friends) does not start its year on 1 January, and its
 * year is a whole number of weeks, so a calendar range would both start on the wrong day and cut
 * the last week in half — which leaves months incomparable between years. Falls back to the
 * calendar year where no fiscal calendar is configured.
 */
export const fiscalYearRange = (
  year: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
) => {
  if (!config) {
    return {
      startDate: new Date(Date.UTC(year, 0, 1)),
      endDate: new Date(Date.UTC(year, 11, 31)),
    }
  }
  const startDate = fiscalYearStartDate(year, config)
  // End the day before the next year's first Monday, so the range is whole weeks and adjacent
  // fiscal years neither overlap nor leave a gap.
  const nextStart = fiscalYearStartDate(year + 1, config)
  return { startDate, endDate: new Date(nextStart.getTime() - MS_PER_DAY) }
}

export const lastFiscalYearRange = (
  startDate: Date,
  endDate: Date,
  config: FiscalConfig | null,
): { startDate: Date; endDate: Date } => {
  if (!config) return { startDate: subYears(startDate, 1), endDate: subYears(endDate, 1) }
  const sameDayLastYear = (ms: number) => {
    const monday = utcMondayMs(new Date(ms))
    return lastYearWeekMs(monday, config) + (ms - monday)
  }
  return {
    startDate: new Date(sameDayLastYear(startDate.getTime())),
    endDate: new Date(sameDayLastYear(endDate.getTime() + MS_PER_DAY) - MS_PER_DAY),
  }
}

// ---------------------------------------------------------------------------
// Budget years. Unlike the period functions above, these read a `null` config as the plain
// calendar year — 1 Jan to 31 Dec in 12 calendar months — not the legacy retail calendar, like
// `fiscalYearRange`. Don't mix them with `dateToFiscal(…, null)` or `fiscalPeriodRange(…, null)`.
// ---------------------------------------------------------------------------

/** `fiscalYearRange` as instants: its first moment and the last moment of its last day. */
export function fiscalYearBounds(
  year: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): { yearStart: Date; yearEnd: Date } {
  const { startDate, endDate } = fiscalYearRange(year, config)
  return { yearStart: startDate, yearEnd: new Date(endDate.getTime() + MS_PER_DAY - 1) }
}

/** Share (0–1) of the span from `start` through the whole of `lastDay` that has passed by `now`. */
export function elapsedFraction(start: Date, lastDay: Date, now: Date): number {
  const startMs = start.getTime()
  const endMs = lastDay.getTime() + MS_PER_DAY
  const t = now.getTime()
  if (t >= endMs) return 1
  if (t < startMs) return 0
  return (t - startMs) / (endMs - startMs)
}

/** Share (0–1) of the year `periods` tile that has passed by `now`. */
export const periodsElapsedFraction = (periods: FiscalYearPeriod[], now: Date): number =>
  elapsedFraction(periods[0]!.start, periods[periods.length - 1]!.end, now)

/** The FY `date` falls in, numbered like `fiscalYearRange`; its calendar year without a fiscal calendar. */
export function fiscalYearOf(
  date: Date,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return config ? dateToFiscal(date, config).fiscalYear : date.getUTCFullYear()
}

/**
 * The FY a saved year-long range stands for: the one containing its midpoint. A range saved as the
 * calendar year, before the company had a fiscal calendar, starts in the previous FY but is meant as
 * the one it mostly covers.
 */
export function fiscalYearOfRange(
  start: Date,
  end: Date,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): number {
  return fiscalYearOf(new Date((start.getTime() + end.getTime()) / 2), config)
}

/**
 * A slice of a fiscal year; `end` is its last day at UTC midnight, like `fiscalYearRange`.
 * `monthStart` is the 1st of the month it represents (`fiscalPeriodMonthStart`), its chart x.
 */
export type FiscalYearPeriod = { start: Date; end: Date; monthStart: Date; label: string }

/**
 * The 12 periods FY`year` is phased into: with a fiscal calendar its fiscal periods (whole weeks,
 * the last carrying week 53), labelled by the month each represents; otherwise the calendar
 * months. Either way they tile `fiscalYearRange(year, config)` with no gap or overlap.
 */
export function fiscalYearPeriods(
  year: number,
  config: FiscalConfig | null = getStoredFiscalConfig(),
): FiscalYearPeriod[] {
  return Array.from({ length: 12 }, (_, p) => {
    const monthStart = fiscalPeriodMonthStart(year, p, config)
    const label = formatDate(monthStart, DateFormatType.Month)
    if (!config) {
      return { start: monthStart, end: new Date(Date.UTC(year, p + 1, 0)), monthStart, label }
    }
    const mondays = fiscalPeriodWeekMondays(year, p, config)
    return {
      start: mondays[0]!,
      end: new Date(mondays[mondays.length - 1]!.getTime() + 6 * MS_PER_DAY),
      monthStart,
      label,
    }
  })
}

/** Index of the period containing `date`, or -1 when it falls outside them all. */
export function periodIndexOf(date: Date, periods: FiscalYearPeriod[]): number {
  const t = date.getTime()
  const last = periods[periods.length - 1]!
  if (t < periods[0]!.start.getTime() || t >= last.end.getTime() + MS_PER_DAY) return -1
  return periods.findIndex((p) => t < p.end.getTime() + MS_PER_DAY)
}

/**
 * Map a date to its fiscal year + 1-indexed week for a company's fiscal calendar.
 * Counts whole Monday-weeks from the fiscal-year anchor, so 53-week years yield week 53 naturally.
 */
export function dateToFiscalWeek(date: Date, config: FiscalConfig): FiscalWeek {
  const mondayMs = utcMondayMs(date)
  let fiscalYear = date.getUTCFullYear()
  let startMs = fiscalYearStartMondayMs(config.anchor, fiscalYear)
  if (mondayMs < startMs) {
    fiscalYear -= 1
    startMs = fiscalYearStartMondayMs(config.anchor, fiscalYear)
  } else if (mondayMs >= fiscalYearStartMondayMs(config.anchor, fiscalYear + 1)) {
    fiscalYear += 1
    startMs = fiscalYearStartMondayMs(config.anchor, fiscalYear)
  }
  const week = Math.round((mondayMs - startMs) / MS_PER_WEEK) + 1
  return { fiscalYear, week }
}

/** Week-format `DateFormatType`s this module renders in fiscal numbering. */
export const FISCAL_WEEK_FORMATS: ReadonlySet<DateFormatType> = new Set([
  DateFormatType.Week,
  DateFormatType.WeekShort,
  DateFormatType.WeekNumber,
  DateFormatType.WeekYear,
  DateFormatType.WeekMonthYear,
  DateFormatType.GraphWeek,
])

/**
 * Fiscal-aware replacement for `formatDate(date, weekFormatType)`. Renders the fiscal week number
 * for the company's calendar, mirroring the ISO layout of each week `DateFormatType`. Any non-week
 * format (or a date that can't be rendered) falls back to `formatDate` unchanged.
 */
export function formatFiscalWeekLabel(
  date: Date,
  config: FiscalConfig,
  formatType: DateFormatType = DateFormatType.Week,
): string {
  if (!FISCAL_WEEK_FORMATS.has(formatType)) return formatDate(date, formatType)
  const { fiscalYear, week } = dateToFiscalWeek(date, config)
  const year = fiscalYearLabel(fiscalYear, config)
  switch (formatType) {
    case DateFormatType.WeekNumber:
      return `${week}`
    case DateFormatType.WeekShort:
      return `W ${week}`
    case DateFormatType.WeekYear:
      return `${week} ${year}`
    case DateFormatType.WeekMonthYear:
      return `Week ${week} - ${formatDate(date, DateFormatType.MonthYearFull)}`
    case DateFormatType.GraphWeek:
      // Tooltips pair this with the week's date range, so no "from <date>" here.
      return `Week ${week}, ${year}`
    case DateFormatType.Week:
    default:
      return `Week ${week}`
  }
}
